import type { LoggerInterface } from '@zeed/zeed'
import type Tesseract from 'tesseract.js'
import { Logger } from '@zeed/zeed'

const log: LoggerInterface = Logger('ocr:worker')

let worker: Tesseract.Worker | undefined
let workerLangs: string | undefined

export interface OcrWorkerOptions {
  /** Languages, e.g. 'deu', 'eng', 'deu+eng' (default: 'deu+eng') */
  lang?: string
  /** Tesseract.js worker options passed to createWorker */
  workerOptions?: Partial<Tesseract.WorkerOptions>
}

/**
 * Module-level defaults applied to every {@link getOcrWorker} call. Set once
 * at app startup with {@link configureOcr} to point Tesseract at locally
 * hosted assets — otherwise tesseract.js falls back to its public CDN
 * defaults (jsdelivr + tessdata.projectnaptha.com).
 */
let defaultWorkerOptions: Partial<Tesseract.WorkerOptions> = {}

/**
 * Configure module-level defaults for the persistent worker. The host app
 * usually provides {@link Tesseract.WorkerOptions.workerPath workerPath},
 * {@link Tesseract.WorkerOptions.corePath corePath} and
 * {@link Tesseract.WorkerOptions.langPath langPath} so all assets are
 * served from the app's own origin.
 */
export function configureOcr(options: Partial<Tesseract.WorkerOptions>): void {
  defaultWorkerOptions = { ...defaultWorkerOptions, ...options }
}

/** Get or create persistent Tesseract worker. Lazy — first call initializes. */
export async function getOcrWorker(options: OcrWorkerOptions = {}): Promise<Tesseract.Worker> {
  const lang = options.lang ?? 'deu+eng'

  if (worker && workerLangs === lang)
    return worker

  if (worker) {
    log('reinitializing worker for language change', workerLangs, '->', lang)
    await worker.reinitialize(lang)
    workerLangs = lang
    return worker
  }

  const mergedOptions = { ...defaultWorkerOptions, ...options.workerOptions }
  log('creating worker for', lang)
  const { createWorker } = await import('tesseract.js')
  worker = await createWorker(lang, undefined, mergedOptions)
  workerLangs = lang
  return worker
}

/** Terminate the persistent worker (cleanup). */
export async function terminateOcrWorker(): Promise<void> {
  if (worker) {
    await worker.terminate()
    worker = undefined
    workerLangs = undefined
    log('worker terminated')
  }
}
